import React, { useEffect, useRef, useState, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  listDAppConfigs,
  DAppConfig,
  listMappings,
  MappingRecord,
  listPolicies,
  addPolicies,
  updatePolicy,
  deletePolicy,
  PolicyRecord,
  getPolicyTypeFromId,
} from '../services/trustline';
import { AVAILABLE_POLICIES, isEmailAuthPolicyType, isMultiAuthPolicyType } from '../config/policies';
import { POLICY_GROUPS, policyGroupOf } from '../config/policyGroups';
import { byModifiedThenName, formatDate, latest, NO_DATE_HINT } from '../utils/dates';
import { useAuth } from '../contexts/AuthContext';
import { suggestPolicyName } from '../utils/nameSuggestions';
import {
  EMAIL_DOMAINS_HINT,
  EMAIL_DOMAINS_PLACEHOLDER,
  formatAllowedEmails,
  validateEmailsOrDomains,
} from '../utils/emailDomainValidation';
import ApproverEmailDisclaimer from '../components/ApproverEmailDisclaimer';
import ConfirmDeleteModal from '../components/ConfirmDeleteModal';
import CopyableId from '../components/CopyableId';
import ActionBanner from '../components/ActionBanner';
import DashIconButton from '../components/DashIconButton';
import '../components/layout/DashboardLayout.css';

const PoliciesPage: React.FC = () => {
  const { email } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const [policies, setPolicies] = useState<PolicyRecord[]>([]);
  const [mappings, setMappings] = useState<MappingRecord[]>([]);
  const [dApps, setDApps] = useState<DAppConfig[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const [showCreate, setShowCreate] = useState(false);
  const [newName, setNewName] = useState('');
  const [newType, setNewType] = useState('');
  const [newEmails, setNewEmails] = useState('');
  const [emailError, setEmailError] = useState('');
  const [newValidatorEmails, setNewValidatorEmails] = useState('');
  const [newValidatorEmailError, setNewValidatorEmailError] = useState('');
  const [newRequiredValidators, setNewRequiredValidators] = useState('1');
  const [creating, setCreating] = useState(false);
  const nameTouchedRef = useRef(false);
  const lastSuggestionRef = useRef('');

  const [editing, setEditing] = useState<PolicyRecord | null>(null);
  const [editName, setEditName] = useState('');
  const [editConfigJson, setEditConfigJson] = useState('');
  const [editEmails, setEditEmails] = useState('');
  const [editEmailError, setEditEmailError] = useState('');
  const [editValidatorEmails, setEditValidatorEmails] = useState('');
  const [editValidatorEmailError, setEditValidatorEmailError] = useState('');
  const [editRequiredValidators, setEditRequiredValidators] = useState('0');
  const [saving, setSaving] = useState(false);

  const [deleteTarget, setDeleteTarget] = useState<PolicyRecord | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    setLoading(true);
    setError(null);
    const [res, mappingsRes, dAppsRes] = await Promise.all([listPolicies(), listMappings(), listDAppConfigs()]);
    setDApps(dAppsRes.success && dAppsRes.data?.dAppConfigs ? dAppsRes.data.dAppConfigs : []);
    if (res.success && res.data?.policies) {
      setPolicies(res.data.policies);
    } else {
      setError(res.error || 'Failed to load policies');
    }
    setMappings(mappingsRes.success && mappingsRes.data?.mappings ? mappingsRes.data.mappings : []);
    setLoading(false);
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const selectedOption = AVAILABLE_POLICIES.find((p) => p.id === newType);

  useEffect(() => {
    if (!newType) {
      if (!nameTouchedRef.current) {
        setNewName('');
        lastSuggestionRef.current = '';
      }
      return;
    }

    const suggestion = suggestPolicyName({
      policyType: newType,
      email: email || undefined,
      existingNames: policies.map((p) => p.name),
    });

    setNewName((prev) => {
      const shouldApply =
        !nameTouchedRef.current ||
        prev.trim() === '' ||
        prev === lastSuggestionRef.current;

      lastSuggestionRef.current = suggestion;

      if (shouldApply && prev !== suggestion) {
        nameTouchedRef.current = false;
        return suggestion;
      }
      return prev;
    });
  }, [newType, email, policies]);

  const resetCreateForm = () => {
    setShowCreate(false);
    setNewName('');
    setNewType('');
    setNewEmails('');
    setEmailError('');
    setNewValidatorEmails('');
    setNewValidatorEmailError('');
    setNewRequiredValidators('1');
    nameTouchedRef.current = false;
    lastSuggestionRef.current = '';
  };

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newName.trim() || !newType) return;
    setError(null);
    setSuccess(null);
    setEmailError('');
    setNewValidatorEmailError('');

    if (selectedOption?.customizations.emails) {
      const validationError = validateEmailsOrDomains(newEmails);
      if (validationError) {
        setEmailError(validationError);
        return;
      }
    }

    let required = 0;
    if (selectedOption?.customizations.multiAuth) {
      required = Number.parseInt(newRequiredValidators, 10);
      if (!Number.isFinite(required) || required < 0 || !Number.isInteger(required)) {
        setNewValidatorEmailError('Enter a whole number of required validators (0 or more)');
        return;
      }
      if (required > 0 || newValidatorEmails.trim()) {
        const validationError = validateEmailsOrDomains(newValidatorEmails);
        if (validationError) {
          setNewValidatorEmailError(validationError);
          return;
        }
      }
    }

    setCreating(true);

    const apiType = getPolicyTypeFromId(newType);
    let config: Record<string, any> = {};
    if (selectedOption?.customizations.emails) {
      const emails = newEmails
        .split(',')
        .map((s) => s.trim().toLowerCase())
        .filter(Boolean);
      config = {
        allowedEmailDomains: emails,
        requireEmailVerification: true,
        allowsCustomization: true,
      };
    }
    if (selectedOption?.customizations.multiAuth) {
      config.requiredAdditionalAuthentications = required;
      config.allowedAdditionalAuthenticationEmailDomains = newValidatorEmails
        .split(',')
        .map((s) => s.trim().toLowerCase())
        .filter(Boolean);
    }

    const res = await addPolicies([{ name: newName.trim(), type: apiType, config }]);
    setCreating(false);
    if (res.success) {
      setSuccess('Policy created.');
      resetCreateForm();
      refresh();
    } else {
      setError(res.error || 'Failed to create policy');
    }
  };

  const openEdit = useCallback((policy: PolicyRecord) => {
    setEditing(policy);
    setEditName(policy.name);
    setEditConfigJson(JSON.stringify(policy.config || {}, null, 2));
    const domains = policy.config?.allowedEmailDomains;
    setEditEmails(Array.isArray(domains) ? domains.join(', ') : '');
    setEditEmailError('');
    const validators = policy.config?.allowedAdditionalAuthenticationEmailDomains;
    setEditValidatorEmails(Array.isArray(validators) ? validators.join(', ') : '');
    setEditValidatorEmailError('');
    setEditRequiredValidators(
      typeof policy.config?.requiredAdditionalAuthentications === 'number'
        ? String(policy.config.requiredAdditionalAuthentications)
        : '0'
    );
    setSuccess(null);
    setError(null);
    setShowCreate(false);
  }, []);

  // Deep-link from dApp mappings: /policies?edit=<policyId>
  const editParam = searchParams.get('edit');
  useEffect(() => {
    if (loading || !editParam) return;

    const policy = policies.find((p) => p.policyId === editParam);
    if (policy) {
      openEdit(policy);
    } else if (policies.length > 0) {
      setError(`Policy not found: ${editParam}`);
    } else {
      return;
    }

    const next = new URLSearchParams(searchParams);
    next.delete('edit');
    setSearchParams(next, { replace: true });
  }, [loading, policies, editParam, searchParams, setSearchParams, openEdit]);

  const handleSaveEdit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editing) return;
    setError(null);
    setEditEmailError('');
    setEditValidatorEmailError('');

    let config: Record<string, any>;
    if (isEmailAuthPolicyType(editing.type)) {
      const validationError = validateEmailsOrDomains(editEmails);
      if (validationError) {
        setEditEmailError(validationError);
        return;
      }
      config = {
        ...editing.config,
        allowedEmailDomains: editEmails
          .split(',')
          .map((s) => s.trim().toLowerCase())
          .filter(Boolean),
        requireEmailVerification: true,
      };
      if (isMultiAuthPolicyType(editing.type)) {
        const required = Number.parseInt(editRequiredValidators, 10);
        if (!Number.isFinite(required) || required < 0 || !Number.isInteger(required)) {
          setEditValidatorEmailError('Enter a whole number of required validators (0 or more)');
          return;
        }
        if (required > 0 || editValidatorEmails.trim()) {
          const validatorError = validateEmailsOrDomains(editValidatorEmails);
          if (validatorError) {
            setEditValidatorEmailError(validatorError);
            return;
          }
        }
        config.requiredAdditionalAuthentications = required;
        config.allowedAdditionalAuthenticationEmailDomains = editValidatorEmails
          .split(',')
          .map((s) => s.trim().toLowerCase())
          .filter(Boolean);
      }
    } else {
      try {
        config = JSON.parse(editConfigJson);
      } catch {
        setError('Invalid JSON in config');
        return;
      }
    }

    setSaving(true);
    const res = await updatePolicy({
      policyId: editing.policyId,
      name: editName.trim() !== editing.name ? editName.trim() : undefined,
      config,
    });
    setSaving(false);
    if (res.success) {
      setSuccess('Policy updated.');
      setEditing(null);
      refresh();
    } else {
      setError(res.error || 'Update failed');
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    setDeleteError(null);
    const res = await deletePolicy(deleteTarget.policyId);
    setDeleting(false);
    if (res.success) {
      setDeleteTarget(null);
      setSuccess('Policy deleted.');
      refresh();
    } else {
      setDeleteError(res.error || 'Delete failed (policy may still be referenced by mappings)');
    }
  };

  if (loading) {
    return <div className="loading">Loading policies…</div>;
  }

  return (
    <div>
      <div className="dash-page-header">
        <h2>My Policies</h2>
        <p>
          Client-level policies shared across your dApps. Create policies here, then map them to
          contract methods from a dApp detail page.
        </p>
      </div>

      {error && <div className="dash-error">{error}</div>}
      {success && <div className="dash-success">{success}</div>}

      <ActionBanner
        title="Create a new policy"
        text="A policy decides which transactions Trustline approves: all of them, only after an email code (OTP) from allowed addresses, or none. Map it to contract methods from a dApp."
        label={showCreate ? 'Cancel' : 'Create a new policy'}
        onClick={() => {
          if (showCreate) {
            resetCreateForm();
          } else {
            setEditing(null);
            setShowCreate(true);
          }
        }}
      />

      {showCreate && (
        <form className="dash-card" onSubmit={handleCreate}>
          <h3>New policy</h3>
          <div className="dash-form-group">
            <label htmlFor="pol-type">Policy type *</label>
            <select
              id="pol-type"
              value={newType}
              onChange={(e) => {
                nameTouchedRef.current = false;
                setNewType(e.target.value);
                setEmailError('');
              }}
              title={selectedOption?.description || ''}
              required
            >
              <option value="">Select a policy type</option>
              {AVAILABLE_POLICIES.map((p) => (
                <option key={p.id} value={p.id} title={p.description}>
                  {p.name}
                </option>
              ))}
            </select>
            {selectedOption && (
              <div className="dash-form-description">
                <span className="dash-form-description-label">Description:</span>
                {selectedOption.description}
              </div>
            )}
          </div>
          <div className="dash-form-group">
            <label htmlFor="pol-name">Policy name *</label>
            <input
              id="pol-name"
              value={newName}
              onChange={(e) => {
                nameTouchedRef.current = true;
                setNewName(e.target.value);
              }}
              placeholder="Suggested from policy type"
              required
            />
            {!nameTouchedRef.current && newName && (
              <small className="dash-form-hint">
                Suggested name — edit freely if you prefer another.
              </small>
            )}
          </div>
          {selectedOption?.customizations.emails && (
            <div className="dash-form-group">
              <label htmlFor="pol-emails">
                {selectedOption.customizations.multiAuth
                  ? 'Initiator email domains/addresses (comma-separated) *'
                  : 'Email domains/addresses (comma-separated) *'}
              </label>
              <input
                id="pol-emails"
                value={newEmails}
                onChange={(e) => {
                  setNewEmails(e.target.value);
                  if (emailError) setEmailError('');
                }}
                placeholder={EMAIL_DOMAINS_PLACEHOLDER}
                className={emailError ? 'error' : ''}
                required
              />
              {emailError && <p className="dash-form-field-error">{emailError}</p>}
              <small className="dash-form-hint">{EMAIL_DOMAINS_HINT}</small>
              <ApproverEmailDisclaimer />
            </div>
          )}
          {selectedOption?.customizations.multiAuth && (
            <>
              <div className="dash-form-group">
                <label htmlFor="pol-required-validators">Required additional validators *</label>
                <input
                  id="pol-required-validators"
                  type="number"
                  min={0}
                  step={1}
                  value={newRequiredValidators}
                  onChange={(e) => {
                    setNewRequiredValidators(e.target.value);
                    if (newValidatorEmailError) setNewValidatorEmailError('');
                  }}
                  required
                />
                <small className="dash-form-hint">
                  How many distinct validators must approve after the initiator authenticates.
                </small>
              </div>
              <div className="dash-form-group">
                <label htmlFor="pol-validator-emails">
                  Validator email domains/addresses (comma-separated)
                  {Number.parseInt(newRequiredValidators, 10) > 0 ? ' *' : ''}
                </label>
                <input
                  id="pol-validator-emails"
                  value={newValidatorEmails}
                  onChange={(e) => {
                    setNewValidatorEmails(e.target.value);
                    if (newValidatorEmailError) setNewValidatorEmailError('');
                  }}
                  placeholder={EMAIL_DOMAINS_PLACEHOLDER}
                  className={newValidatorEmailError ? 'error' : ''}
                  required={Number.parseInt(newRequiredValidators, 10) > 0}
                />
                {newValidatorEmailError && (
                  <p className="dash-form-field-error">{newValidatorEmailError}</p>
                )}
                <small className="dash-form-hint">
                  {EMAIL_DOMAINS_HINT} Full email addresses can be notified when approval is waiting;
                  domains or &quot;*&quot; only authorize without targeting a notification.
                </small>
                <ApproverEmailDisclaimer />
              </div>
            </>
          )}
          <button type="submit" className="dash-btn dash-btn-primary" disabled={creating}>
            {creating ? 'Creating…' : 'Create'}
          </button>
          <button
            type="button"
            className="dash-btn dash-btn-secondary"
            onClick={resetCreateForm}
            style={{ marginLeft: '0.5rem' }}
          >
            Cancel
          </button>
        </form>
      )}

      {editing && (
        <form className="dash-card" onSubmit={handleSaveEdit}>
          <h3>Edit: {editing.name}</h3>
          <div className="dash-form-group">
            <label>Type</label>
            <input value={editing.type} disabled />
            {AVAILABLE_POLICIES.find((p) => getPolicyTypeFromId(p.id) === editing.type) && (
              <div className="dash-form-description">
                <span className="dash-form-description-label">Description:</span>
                {
                  AVAILABLE_POLICIES.find((p) => getPolicyTypeFromId(p.id) === editing.type)!
                    .description
                }
              </div>
            )}
          </div>
          <div className="dash-form-group">
            <label htmlFor="edit-name">Name</label>
            <input
              id="edit-name"
              value={editName}
              onChange={(e) => setEditName(e.target.value)}
            />
          </div>
          {isEmailAuthPolicyType(editing.type) ? (
            <>
              <div className="dash-form-group">
                <label htmlFor="edit-emails">
                  {isMultiAuthPolicyType(editing.type)
                    ? 'Initiator email domains/addresses (comma-separated)'
                    : 'Email domains/addresses (comma-separated)'}
                </label>
                <input
                  id="edit-emails"
                  value={editEmails}
                  onChange={(e) => {
                    setEditEmails(e.target.value);
                    if (editEmailError) setEditEmailError('');
                  }}
                  placeholder={EMAIL_DOMAINS_PLACEHOLDER}
                  className={editEmailError ? 'error' : ''}
                />
                {editEmailError && <p className="dash-form-field-error">{editEmailError}</p>}
                <small className="dash-form-hint">{EMAIL_DOMAINS_HINT}</small>
                <ApproverEmailDisclaimer />
              </div>
              {isMultiAuthPolicyType(editing.type) && (
                <>
                  <div className="dash-form-group">
                    <label htmlFor="edit-required-validators">Required additional validators</label>
                    <input
                      id="edit-required-validators"
                      type="number"
                      min={0}
                      step={1}
                      value={editRequiredValidators}
                      onChange={(e) => {
                        setEditRequiredValidators(e.target.value);
                        if (editValidatorEmailError) setEditValidatorEmailError('');
                      }}
                    />
                    <small className="dash-form-hint">
                      How many distinct validators must approve after the initiator authenticates.
                    </small>
                  </div>
                  <div className="dash-form-group">
                    <label htmlFor="edit-validator-emails">
                      Validator email domains/addresses (comma-separated)
                    </label>
                    <input
                      id="edit-validator-emails"
                      value={editValidatorEmails}
                      onChange={(e) => {
                        setEditValidatorEmails(e.target.value);
                        if (editValidatorEmailError) setEditValidatorEmailError('');
                      }}
                      placeholder={EMAIL_DOMAINS_PLACEHOLDER}
                      className={editValidatorEmailError ? 'error' : ''}
                    />
                    {editValidatorEmailError && (
                      <p className="dash-form-field-error">{editValidatorEmailError}</p>
                    )}
                    <small className="dash-form-hint">
                      {EMAIL_DOMAINS_HINT} Full email addresses can be notified when approval is
                      waiting; domains or &quot;*&quot; only authorize without targeting a
                      notification.
                    </small>
                    <ApproverEmailDisclaimer />
                  </div>
                </>
              )}
            </>
          ) : (
            <div className="dash-form-group">
              <label htmlFor="edit-config">Config (JSON)</label>
              <textarea
                id="edit-config"
                rows={8}
                value={editConfigJson}
                onChange={(e) => setEditConfigJson(e.target.value)}
                style={{ fontFamily: 'monospace' }}
              />
            </div>
          )}
          <div className="dash-actions">
            <button type="submit" className="dash-btn dash-btn-primary" disabled={saving}>
              {saving ? 'Saving…' : 'Save'}
            </button>
            <button
              type="button"
              className="dash-btn dash-btn-secondary"
              onClick={() => setEditing(null)}
            >
              Cancel
            </button>
          </div>
        </form>
      )}

      <div className="dash-card">
        {policies.length === 0 ? (
          <p className="dash-empty">No policies yet, create one above.</p>
        ) : (
          POLICY_GROUPS.map((g) => ({
            ...g,
            items: policies.filter((p) => policyGroupOf(p.type) === g.key).sort(byModifiedThenName((p) => latest(p.updatedAt, p.createdAt), (p) => p.name)),
          }))
            .filter((g) => g.items.length > 0 || g.key === 'blocked')
            .map((g) => (
              <section key={g.key} className="dash-policy-group">
                <h3 className="dash-policy-group-title">{g.title}</h3>
                <p className="dash-policy-item-desc">{g.description}</p>
                {g.items.length === 0 ? (
                  <p className="dash-form-hint">No blocked policy: a method left without a policy is blocked.</p>
                ) : (
                  <div className="dash-table-wrap">
                    <table className="dash-table">
                      <thead>
                        <tr>
                          <th></th>
                          <th>Last modified</th>
                          <th>Policy name</th>
                          <th>Policy ID</th>
                          {g.key === 'otp' && <th>Authentication</th>}
                          {g.key === 'other' && <th>Type</th>}
                          <th>Date created</th>
                          <th>Methods using it</th>
                        </tr>
                      </thead>
                      <tbody>
                        {g.items.map((p) => {
                          const emails: string[] = Array.isArray(p.config?.allowedEmailDomains)
                            ? p.config.allowedEmailDomains
                            : [];
                          const validatorEmails: string[] = Array.isArray(
                            p.config?.allowedAdditionalAuthenticationEmailDomains
                          )
                            ? p.config.allowedAdditionalAuthenticationEmailDomains
                            : [];
                          const requiredValidators =
                            typeof p.config?.requiredAdditionalAuthentications === 'number'
                              ? p.config.requiredAdditionalAuthentications
                              : null;
                          // Per contract the policy is used on: how many of its methods map to this policy.
                          const usedOn = mappings.filter((m) => m.policyIds?.includes(p.policyId));
                          const byDApp = Array.from(new Set(usedOn.map((m) => m.dAppConfigId))).map((id) => ({
                            id,
                            name: dApps.find((d) => d.id === id)?.name ?? id,
                            uses: usedOn.filter((m) => m.dAppConfigId === id).length,
                          }));
                          return (
                            <tr key={p.policyId}>
                              <td className="dash-actions-cell">
                                <div className="dash-actions">
                                  <DashIconButton action="edit" title={`Edit ${p.name}`} onClick={() => openEdit(p)} />
                                  <DashIconButton
                                    action="delete"
                                    title={`Delete ${p.name}`}
                                    onClick={() => {
                                      setDeleteError(null);
                                      setDeleteTarget(p);
                                    }}
                                  />
                                </div>
                              </td>
                              <td title={latest(p.updatedAt, p.createdAt) ? undefined : NO_DATE_HINT}>{formatDate(latest(p.updatedAt, p.createdAt))}</td>
                              <td>{p.name}</td>
                              <td>
                                <CopyableId value={p.policyId} label="Policy ID" />
                              </td>
                              {g.key === 'otp' && (
                                <td>
                                  {isMultiAuthPolicyType(p.type) ? (
                                    <>
                                      <div>
                                        <span className="dash-form-hint">Initiator: </span>
                                        {formatAllowedEmails(emails)}
                                      </div>
                                      <div>
                                        <span className="dash-form-hint">Validators: </span>
                                        {requiredValidators ?? 0} required
                                        {validatorEmails.length > 0
                                          ? ` · ${formatAllowedEmails(validatorEmails)}`
                                          : ''}
                                      </div>
                                    </>
                                  ) : (
                                    formatAllowedEmails(emails)
                                  )}
                                </td>
                              )}
                              {g.key === 'other' && <td className="dash-mono">{p.type}</td>}
                              <td title={p.createdAt ? undefined : NO_DATE_HINT}>{formatDate(p.createdAt)}</td>
                              <td>
                                {byDApp.length === 0
                                  ? '0'
                                  : byDApp.map((u) => (
                                      <div key={u.id}>
                                        {u.uses}
                                        {byDApp.length > 1 && <span className="dash-form-hint"> {u.name}</span>}
                                      </div>
                                    ))}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                )}
              </section>
            ))
        )}
      </div>

      <ConfirmDeleteModal
        open={!!deleteTarget}
        title="Delete policy?"
        description={
          <p>
            Delete policy <strong>{deleteTarget?.name}</strong>? This will fail if any mapping
            still references it.
          </p>
        }
        confirmLabel="Delete policy"
        loading={deleting}
        error={deleteError}
        onCancel={() => setDeleteTarget(null)}
        onConfirm={handleDelete}
      />
    </div>
  );
};

export default PoliciesPage;
